/**
 * Ingredients Section Drawer Functionality
 */
(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-ingredients-section]';
  const CARD_SELECTOR = '[data-ingredient-card]';
  const CARD_TRIGGER_SELECTOR = '[data-ingredient-summary]';
  const DRAWER_SELECTOR = '[data-ingredients-drawer]';
  const DRAWER_CONTENT_SELECTOR = '[data-ingredients-drawer-content]';
  const DRAWER_HEADING_SELECTOR = '[data-ingredients-drawer-heading]';

  const initIngredientsSection = (section) => {
    if (!section || section.dataset.ingredientsInitialized === 'true') {
      return;
    }

    const drawer = section.querySelector(DRAWER_SELECTOR);
    if (!drawer) {
      return;
    }

    const contentTarget = drawer.querySelector(DRAWER_CONTENT_SELECTOR);
    const drawerInner = drawer.querySelector('.drawer__inner');
    const drawerHeading = drawer.querySelector(DRAWER_HEADING_SELECTOR);
    const defaultHeading = drawerHeading ? drawerHeading.textContent.trim() : '';

    const parentSectionElement = section.closest('[data-section-id]');
    const parentSectionId = parentSectionElement ? parentSectionElement.dataset.sectionId : section.id || '';
    if (parentSectionId) {
      drawer.dataset.parentSection = parentSectionId;
    }

    if (!drawer.dataset.ingredientsDrawerMounted) {
      drawer.dataset.ingredientsDrawerMounted = 'true';
      document.body.appendChild(drawer);
    }

    let activeSummary = null;
    let previouslyFocusedElement = null;

    const openDrawer = (headingText, html) => {
      if (!contentTarget) return;
      contentTarget.innerHTML = html;
      // Reset scroll position to top
      contentTarget.scrollTop = 0;
      if (drawerHeading) {
        drawerHeading.textContent = headingText || defaultHeading;
      }
      if (!drawer.classList.contains('animate')) {
        drawer.classList.add('animate');
      }
      drawer.classList.add('active');
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');

      // Ensure scrollbar is visible
      if (contentTarget) {
        contentTarget.style.overflowY = 'scroll';
        contentTarget.style.overflowX = 'hidden';
        contentTarget.style.maxHeight = '100%';
        contentTarget.style.height = '100%';
        contentTarget.style.scrollbarWidth = 'auto';
        contentTarget.style.scrollbarColor = '#999999 #e0e0e0';
      }

      const focusTarget = contentTarget;
      const focusContainer = drawerInner || drawer;

      const handleTransitionEnd = () => {
        if (typeof trapFocus === 'function') {
          trapFocus(focusContainer, focusTarget);
        }
        focusTarget.focus();
      };

      drawer.addEventListener('transitionend', handleTransitionEnd, { once: true });
      requestAnimationFrame(() => {
        focusTarget.focus();
      });
    };

    const closeDrawer = () => {
      drawer.classList.remove('active');
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (contentTarget) {
        contentTarget.innerHTML = '';
      }
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(previouslyFocusedElement || document.body);
      }
      if (drawerHeading) {
        drawerHeading.textContent = defaultHeading;
      }
      if (activeSummary) {
        activeSummary.setAttribute('aria-expanded', 'false');
        activeSummary.focus();
        activeSummary = null;
      }
    };

    const handleDrawerClick = (event) => {
      const closeControl = event.target.closest('[data-ingredients-drawer-close]');
      if (closeControl) {
        event.preventDefault();
        closeDrawer();
      }
    };

    drawer.addEventListener('click', handleDrawerClick);

    drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
        event.preventDefault();
        closeDrawer();
      }
    });

    const triggers = Array.from(section.querySelectorAll(CARD_TRIGGER_SELECTOR));

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const card = trigger.closest(CARD_SELECTOR);
        if (!card) return;

        const targetId = trigger.dataset.ingredientDrawerTarget;
        if (!targetId) return;

        const source = card.querySelector(`#${CSS.escape(targetId)} [data-ingredient-drawer-source]`);
        if (!source) return;

        previouslyFocusedElement = document.activeElement;
        activeSummary = trigger;
        trigger.setAttribute('aria-expanded', 'true');

        const headingText = trigger.dataset.ingredientDrawerHeading || trigger.textContent.trim();

        openDrawer(headingText, source.innerHTML);
      });
    });

    section.dataset.ingredientsInitialized = 'true';
  };

  const initAllIngredientsSections = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => initIngredientsSection(section));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllIngredientsSections);
  } else {
    initAllIngredientsSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) {
      initIngredientsSection(section);
    } else {
      initAllIngredientsSections();
    }
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) {
      initIngredientsSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (!section) return;
    const parentSectionElement = section.closest('[data-section-id]');
    const parentSectionId = parentSectionElement ? parentSectionElement.dataset.sectionId : section.id || '';
    if (!parentSectionId) return;
    document
      .querySelectorAll(`[data-ingredients-drawer][data-parent-section="${parentSectionId}"]`)
      .forEach((drawer) => drawer.remove());
  });
})();
